import { Controller, useFormContext } from 'react-hook-form';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import {
  ExpirationMode,
  ExpirationPeriodUnits,
  ExpirationType,
} from '@loyalty/shared-common/gamification-api-client';
import { Feature } from '@loyalty/shared-common';
import {
  FormControlLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Switch,
  TextField,
} from '@mui/material';
import { QlfFormRow, QlfNumberField } from '@qualifio/ui-commons';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFeatures } from '../features/FeaturesContext';

type ExpirationKind = 'level' | 'currency';

export function useLevelsExpirationMode() {
  const { t } = useTranslation();
  return useMemo<{ [key in ExpirationMode]: string }>(
    () => ({
      [ExpirationMode.REEVALUATE]: t('levels.enums.expirationMode.reevaluate'),
      [ExpirationMode.RESET]: t('levels.enums.expirationMode.reset'),
    }),
    [t],
  );
}

export function ExpirationForm({ kind }: { kind: ExpirationKind }) {
  const { control, watch } = useFormContext();
  const { features } = useFeatures();
  const { enabled: weeklyExpirationEnabled } = features[Feature.WEEKLY_EXPIRATION];
  const { t } = useTranslation();

  const type = watch('expiration.type');
  const sourceTranslationExpiration = {
    [ExpirationType.ROLLING]: t('levels.enums.expirationType.ROLLING'),
    [ExpirationType.CALENDAR]: t('levels.enums.expirationType.CALENDAR'),
    [ExpirationType.NONE]: t('levels.enums.expirationType.NONE'),
    [ExpirationType.END_OF_MONTH]: t('levels.enums.expirationType.END_OF_MONTH'),
    ...(weeklyExpirationEnabled
      ? { [ExpirationType.END_OF_WEEK]: t('levels.enums.expirationType.END_OF_WEEK') }
      : {}),
  };

  return (
    <>
      <Controller
        control={control}
        name="expiration.type"
        defaultValue=""
        render={({ field: { ref, ...field } }) => (
          <QlfFormRow
            label={t(
              kind === 'level'
                ? 'levels.inputs.expiration.type'
                : 'currencies.inputs.expiration.type',
            )}
          >
            <TextField select inputRef={ref} {...field} id="level-expiry-id">
              {Object.values(ExpirationType).map(type => (
                <MenuItem value={type} key={type}>
                  {sourceTranslationExpiration[type]}
                </MenuItem>
              ))}
            </TextField>
          </QlfFormRow>
        )}
      />
      <TypeForm type={type} kind={kind} />
      <Controller
        control={control}
        name="retroActive"
        defaultValue={false}
        render={({ field: { ref, ...field } }) => (
          <QlfFormRow label={t('levels.inputs.expiration.retroactive')}>
            <FormControlLabel
              control={<Switch {...field} checked={field.value} />}
              label={
                t(
                  kind === 'level'
                    ? 'levels.inputs.expiration.retroactive.effect'
                    : 'currencies.inputs.expiration.retroactive.effect',
                ) as string
              }
            />
          </QlfFormRow>
        )}
      />
    </>
  );
}

function TypeForm({ type, kind }: { type: ExpirationType; kind: ExpirationKind }) {
  switch (type) {
    case ExpirationType.ROLLING:
      return <RollingExpirationForm kind={kind} />;
    case ExpirationType.CALENDAR:
      return <CalendarExpirationForm kind={kind} />;
    case ExpirationType.END_OF_MONTH:
    case ExpirationType.END_OF_WEEK:
      if (kind === 'level') {
        return <ExpirationModeForm />;
      }
      return <></>;
    default:
      return <></>;
  }
}

function CalendarExpirationForm({ kind }: { kind: ExpirationKind }) {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const { t } = useTranslation();

  return (
    <>
      <Controller
        control={control}
        name="expiration.date"
        defaultValue={new Date().toJSON()}
        render={({ field: { ref, name, onChange, ...field }, fieldState: { error } }) => (
          <DesktopDatePicker
            inputRef={ref}
            inputFormat="dd/MM/yyyy"
            onChange={date => onChange({ target: { value: date?.toJSON() } })}
            {...field}
            renderInput={params => {
              return (
                <QlfFormRow label={t('levels.inputs.expiration.date')}>
                  <TextField
                    {...params}
                    placeholder={t('levels.inputs.expiration.date')}
                    error={!!error}
                    helperText={error ? t('levels.inputs.expiration.date.validation-error') : ''}
                  />
                </QlfFormRow>
              );
            }}
          />
        )}
      />
      <ExpirationPeriodForm type={ExpirationType.CALENDAR} />
      {kind === 'level' && <ExpirationModeForm />}
    </>
  );
}

function RollingExpirationForm({ kind }: { kind: ExpirationKind }) {
  return (
    <>
      <ExpirationPeriodForm type={ExpirationType.ROLLING} />
      {kind === 'level' && <ExpirationModeForm />}
    </>
  );
}

function ExpirationModeForm() {
  const { control } = useFormContext();
  const sourceTranslationsExpirationMode = useLevelsExpirationMode();
  const { t } = useTranslation();

  return (
    <Controller
      control={control}
      name="expiration.expirationMode"
      defaultValue={ExpirationMode.REEVALUATE}
      render={({ field: { ref, ...field } }) => (
        <QlfFormRow label={t('levels.inputs.expiration.mode')}>
          <RadioGroup value={field.value} onChange={field.onChange} row>
            <FormControlLabel
              value={ExpirationMode.REEVALUATE}
              control={<Radio />}
              label={sourceTranslationsExpirationMode[ExpirationMode.REEVALUATE]}
            />
            <FormControlLabel
              value={ExpirationMode.RESET}
              control={<Radio />}
              label={sourceTranslationsExpirationMode[ExpirationMode.RESET]}
            />
          </RadioGroup>
        </QlfFormRow>
      )}
    />
  );
}

function ExpirationPeriodForm({ type }: { type: ExpirationType }) {
  const { control } = useFormContext();
  const { t } = useTranslation();

  const levelExpirationIntervals = [
    { value: ExpirationPeriodUnits.DAY, label: t('currencies.edition.expiration.day') },
    { value: ExpirationPeriodUnits.WEEK, label: t('currencies.edition.expiration.week') },
    { value: ExpirationPeriodUnits.MONTH, label: t('currencies.edition.expiration.month') },
    { value: ExpirationPeriodUnits.YEAR, label: t('currencies.edition.expiration.year') },
  ];

  return (
    <>
      <QlfFormRow
        label={
          type === ExpirationType.ROLLING
            ? t('levels.edition.expiration.after')
            : t('levels.edition.expiration.every')
        }
      >
        <Grid item xs={5} sx={{ display: 'flex', gap: '10px' }}>
          <Controller
            control={control}
            name="expiration.period.value"
            defaultValue={1}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <QlfNumberField
                {...field}
                inputRef={ref}
                min={0}
                max={99999999999}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="expiration.period.unit"
            defaultValue={levelExpirationIntervals[3].value}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <TextField
                {...field}
                select
                inputRef={ref}
                error={!!error}
                helperText={error?.message}
                {...(type === ExpirationType.CALENDAR
                  ? { value: levelExpirationIntervals[3].value, disabled: true }
                  : {})}
              >
                {levelExpirationIntervals.map(type => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>
      </QlfFormRow>
    </>
  );
}
